<template>
  <div class="admin-page">
    <Header />
    <div class="header-actions">
      <h2 class="title"><i class="fa-solid fa-chart-line"></i> Dashboard Financeiro</h2>
      <router-link to="/admin/dashboard" class="btn-back">Voltar ao Painel</router-link>
    </div>

    <div class="financial-stats">
      <div class="stat-card">
        <h4>Receita Total</h4>
        <div class="value">R$ {{ formatAmount(kpis.receitaTotal) }}</div>
      </div>
      <div class="stat-card">
        <h4>Acessos Vendidos</h4>
        <div class="value">{{ kpis.acessosVendidos }}</div>
      </div>
      <div class="stat-card">
        <h4>Faturas Pendentes</h4>
        <div class="value" :class="{ error: kpis.faturasPendentes > 0 }">{{ kpis.faturasPendentes }}</div>
      </div>
    </div>

    <!-- Gráficos -->
    <div class="charts-container">
      <div class="chart-card">
        <h3>Receita Mensal (6 meses)</h3>
        <apexchart type="bar" height="250" :options="chartOptionsRevenue" :series="seriesRevenue"></apexchart>
      </div>
      <div class="chart-card">
        <h3>Acessos Ativos por Parceiro</h3>
        <apexchart type="donut" height="250" :options="chartOptionsAccesses" :series="seriesAccesses"></apexchart>
      </div>
    </div>

    <div class="card-panel">
      <h3><i class="fa-solid fa-file-invoice-dollar"></i> Gestão de Pagamentos</h3>
      <p class="panel-desc">Gere links do Pagar.me ou atualize os status de pagamentos manualmente.</p>
      
      <div class="table-responsive">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Parceiro</th>
              <th>Vencimento</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Ações (Pagar.me)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in invoices" :key="invoice.id">
              <td><strong>{{ invoice.partner }}</strong></td>
              <td>{{ invoice.dueDate }}</td>
              <td>R$ {{ invoice.amount }}</td>
              <td>
                <span :class="['status-badge', invoice.status === 'Pago' ? 'active' : 'inactive']">
                  {{ invoice.status }}
                </span>
              </td>
              <td>
                <button v-if="invoice.status !== 'Pago'" class="btn-action view" title="Gerar Pix" @click="openPaymentModal(invoice, 'pix')"><i class="fa-brands fa-pix"></i></button>
                <button v-if="invoice.status !== 'Pago'" class="btn-action toggle" title="Gerar Link Cartão/Boleto" @click="openPaymentModal(invoice, 'link')"><i class="fa-solid fa-credit-card"></i></button>
                <button v-if="invoice.status !== 'Pago'" class="btn-action text-btn" @click="markAsPaid(invoice)" title="Baixar Manualmente">Baixar</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal de Pagamento -->
    <div class="payment-modal-overlay" v-if="showPaymentModal">
      <div class="payment-modal-content">
        <button class="close-btn" @click="closeModal"><i class="fa-solid fa-times"></i></button>
        <h3><i :class="paymentMode === 'pix' ? 'fa-brands fa-pix' : 'fa-solid fa-credit-card'"></i> {{ paymentMode === 'pix' ? 'Gerar Cobrança Pix' : 'Gerar Link de Pagamento' }}</h3>
        <p class="subtitle-modal">Fatura de <strong>{{ selectedInvoice.partner }}</strong> - R$ {{ selectedInvoice.amount }}</p>

        <div class="modal-body">
          <div v-if="isGeneratingPayment" class="loading-state">
            <div class="spinner-payment"></div>
            <p>Conectando ao Pagar.me...</p>
          </div>

          <div v-else class="result-state">
            <div v-if="paymentMode === 'pix'" class="pix-result">
              <p>Escaneie o QR Code ou copie o código Pix Copia e Cola:</p>
              <img :src="'https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=' + mockPixHash" alt="QR Code" class="qr-code-img" />
              <div class="copy-box">
                <input type="text" readonly :value="mockPixHash" class="copy-input" />
                <button class="btn-copy" @click="copyToClipboard(mockPixHash)">Copiar</button>
              </div>
            </div>

            <div v-else class="link-result">
              <p>Link de Checkout gerado com sucesso!</p>
              <div class="copy-box">
                <input type="text" readonly :value="mockCheckoutLink" class="copy-input" />
                <button class="btn-copy" @click="copyToClipboard(mockCheckoutLink)">Copiar Link</button>
              </div>
            </div>
            
            <p class="success-msg" v-if="copied">Copiado para a área de transferência!</p>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Header from '@/components/Header.vue';
import requestV2 from '@/router/requestV2.js';
import '@/styles/Admin.css';

export default {
  name: 'AdminFinancial',
  components: { Header },
  data() {
    return {
      kpis: {
        receitaTotal: 0,
        parceirosAtivos: 0,
        acessosVendidos: 0,
        faturasPendentes: 0
      },
      invoices: [],
      showPaymentModal: false,
      isGeneratingPayment: false,
      paymentMode: 'pix',
      selectedInvoice: null,
      copied: false,
      mockPixHash: '00020126360014br.gov.bcb.pix0114+55119999999995204000053031565802BR5913Pagar.me6009Sao Paulo62070503***6304E2D8',
      mockCheckoutLink: 'https://checkout.pagar.me/pagamento/12345abcde',
      
      chartOptionsRevenue: {
        chart: { id: 'revenue-chart', toolbar: { show: false }, foreColor: '#aaa', fontFamily: 'Outfit, sans-serif' },
        xaxis: { categories: [] },
        colors: ['#FEC401'],
        plotOptions: { bar: { borderRadius: 4, horizontal: false, columnWidth: '40%' } },
        dataLabels: { enabled: false },
        tooltip: { theme: 'dark' },
        grid: { borderColor: 'rgba(255,255,255,0.1)' }
      },
      seriesRevenue: [{ name: 'Receita (R$)', data: [] }],

      chartOptionsAccesses: {
        chart: { type: 'donut', foreColor: '#fff', fontFamily: 'Outfit, sans-serif' },
        labels: [],
        colors: ['#FEC401', '#2ecc71', '#e74c3c', '#9b59b6', '#3498db'],
        plotOptions: { pie: { donut: { size: '65%' } } },
        dataLabels: { enabled: false },
        stroke: { show: false },
        legend: { position: 'bottom' },
        tooltip: { theme: 'dark' }
      },
      seriesAccesses: [],
    }
  },
  async mounted() {
    await Promise.all([this.loadDashboard(), this.loadInvoices()]);
  },
  methods: {
    formatAmount(value) {
      if (!value && value !== 0) return '0,00';
      return parseFloat(value).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },
    async loadDashboard() {
      try {
        const response = await requestV2.get('/financial/dashboard');
        const data = response.data;
        this.kpis = data.kpis;

        // Atualizar gráfico de receita mensal
        if (data.monthlyRevenue && data.monthlyRevenue.length > 0) {
          const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
          this.chartOptionsRevenue = {
            ...this.chartOptionsRevenue,
            xaxis: {
              categories: data.monthlyRevenue.map(r => {
                const [, m] = r.month.split('-');
                return months[parseInt(m) - 1] || r.month;
              })
            }
          };
          this.seriesRevenue = [{ name: 'Receita (R$)', data: data.monthlyRevenue.map(r => r.total) }];
        }

        // Atualizar gráfico de acessos por parceiro
        if (data.accessesByPartner && data.accessesByPartner.length > 0) {
          this.chartOptionsAccesses = {
            ...this.chartOptionsAccesses,
            labels: data.accessesByPartner.map(p => p.name)
          };
          this.seriesAccesses = data.accessesByPartner.map(p => p.accesses);
        }
      } catch (err) {
        console.error('Erro ao carregar dashboard:', err);
      }
    },
    async loadInvoices() {
      try {
        const response = await requestV2.get('/financial/invoices');
        this.invoices = response.data.map(inv => ({
          ...inv,
          dueDate: new Date(inv.dueDate).toLocaleDateString('pt-BR'),
          amount: parseFloat(inv.amount).toFixed(2).replace('.', ',')
        }));
      } catch (err) {
        console.error('Erro ao carregar faturas:', err);
      }
    },
    async markAsPaid(invoice) {
      if (confirm(`Confirmar baixa manual para o parceiro ${invoice.partner}?`)) {
        try {
          await requestV2.put(`/financial/invoices/${invoice.id}/pay`);
          invoice.status = 'Pago';
        } catch (err) {
          console.error('Erro ao baixar fatura:', err);
          alert('Erro ao baixar fatura.');
        }
      }
    },
    openPaymentModal(invoice, mode) {
      this.selectedInvoice = invoice;
      this.paymentMode = mode;
      this.showPaymentModal = true;
      this.isGeneratingPayment = true;
      this.copied = false;

      // Simula a chamada da API do Pagar.me
      setTimeout(() => {
        this.isGeneratingPayment = false;
      }, 1500);
    },
    closeModal() {
      this.showPaymentModal = false;
      this.selectedInvoice = null;
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.copied = true;
        setTimeout(() => this.copied = false, 3000);
      });
    }
  }
}
</script>

<style scoped>
.financial-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #FEC401;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.stat-card h4 {
  margin: 0 0 10px 0;
  color: #ddd;
  font-weight: 500;
}

.stat-card .value {
  font-size: 2rem;
  font-weight: bold;
  color: #fff;
}

.stat-card .value.error {
  color: #e74c3c;
}

.text-btn {
  font-size: 0.8rem;
  background: rgba(255,255,255,0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

.charts-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.chart-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.chart-card h3 {
  color: #FEC401;
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.1rem;
}

/* Modal Styles */
.payment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.payment-modal-content {
  background: #09105D;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 450px;
  position: relative;
  text-align: center;
  box-shadow: 0 15px 40px rgba(0,0,0,0.5);
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
}

.payment-modal-content h3 {
  color: #FEC401;
  margin-top: 0;
  margin-bottom: 5px;
}

.subtitle-modal {
  color: #ccc;
  margin-bottom: 25px;
  font-size: 0.95rem;
}

.loading-state {
  padding: 30px 0;
}

.spinner-payment {
  border: 4px solid rgba(255,255,255,0.1);
  border-left-color: #FEC401;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

.pix-result .qr-code-img {
  background: #fff;
  padding: 10px;
  border-radius: 8px;
  margin: 10px auto 20px;
  display: block;
}

.copy-box {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.copy-input {
  flex: 1;
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  padding: 10px;
  border-radius: 8px;
  outline: none;
}

.btn-copy {
  background: #FEC401;
  color: #09105D;
  border: none;
  border-radius: 8px;
  padding: 0 15px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy:hover {
  background: #e5b001;
}

.success-msg {
  color: #2ecc71;
  font-size: 0.9rem;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .charts-container {
    grid-template-columns: 1fr;
  }
}
</style>
